import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";

const LABEL_COLUMN_WIDTH = "72px";

function PatternRow({ label, color, cells, valueKey }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${LABEL_COLUMN_WIDTH} repeat(7, minmax(18px, 1fr))`,
        columnGap: 1,
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#62758d" }}>
        {label}
      </Typography>
      {cells.map((cell) => {
        const isActive = Boolean(cell[valueKey]);
        const fillColor = cell.sharedActive && isActive ? "#5f62f2" : color;
        return (
          <Box
            key={`${label}-${cell.key}`}
            sx={{
              justifySelf: "center",
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: isActive ? fillColor : "#ffffff",
              border: isActive ? "none" : "1.5px solid #d8e2ec",
              boxShadow: isActive ? `0 0 0 3px ${fillColor}14` : "none",
            }}
          />
        );
      })}
    </Box>
  );
}

function WeekdayHeader({ cells }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${LABEL_COLUMN_WIDTH} repeat(7, minmax(18px, 1fr))`,
        columnGap: 1,
        alignItems: "center",
      }}
    >
      <Box />
      {cells.map((cell) => (
        <Typography
          key={`weekday-${cell.key}`}
          sx={{
            justifySelf: "center",
            fontSize: "0.66rem",
            color: "#8b9bb0",
            fontWeight: 800,
          }}
        >
          {cell.label}
        </Typography>
      ))}
    </Box>
  );
}

function Legend() {
  const items = [
    { label: "Active", color: "#3fae72", filled: true },
    { label: "Shared day", color: "#5f62f2", filled: true },
    { label: "No activity", color: "#d8e2ec", filled: false },
  ];
  return (
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
      {items.map((item) => (
        <Stack key={item.label} direction="row" spacing={0.7} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: item.filled ? item.color : "#ffffff",
              border: item.filled ? "none" : `1.5px solid ${item.color}`,
            }}
          />
          <Typography sx={{ fontSize: "0.76rem", color: "#7a8ca4", fontWeight: 700 }}>
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function summarizeWeek({ items, partnerName, sprintMode }) {
  const viewerDays = items.filter((item) => item.viewerActive).length;
  const partnerDays = items.filter((item) => item.partnerActive).length;
  const sharedDays = items.filter((item) => item.sharedActive).length;
  if (sprintMode === "challenge") {
    return `Report days so far: You ${viewerDays}, ${partnerName} ${partnerDays}.`;
  }
  if (sharedDays <= 0) return "No days yet where both Daily Reports were submitted.";
  if (sharedDays === 1) return "1 day where both Daily Reports were submitted.";
  return `${sharedDays} days where both Daily Reports were submitted.`;
}

function getLastActivityLine(recentItems = []) {
  const lastActivity = recentItems.find((item) => item?.id === "last-activity");
  if (!lastActivity) return null;
  return `${lastActivity.label}: ${lastActivity.detail}`;
}

export default function SprintWeekPatternCard({
  items = [],
  partnerName = "Partner",
  recentItems = [],
  sprintMode = "support",
}) {
  const hasAnyPattern = items.some((item) => item.viewerActive || item.partnerActive);
  const lastActivityLine = getLastActivityLine(recentItems);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: "8px",
        borderColor: "#dde6ef",
        boxShadow: "0 16px 32px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#102033", mb: 0.5 }}>
        Accountability Log
      </Typography>
      <Typography variant="body2" sx={{ color: "#70839a", mb: 1.7 }}>
        Daily reports across this agreement.
      </Typography>

      <Stack spacing={1.15}>
        <WeekdayHeader cells={items} />
        <PatternRow label="You" color="#3fae72" cells={items} valueKey="viewerActive" />
        <PatternRow label={partnerName} color="#3fae72" cells={items} valueKey="partnerActive" />
      </Stack>

      <Legend />

      <Typography sx={{ mt: 1.4, fontSize: "0.86rem", fontWeight: 800, color: "#102033" }}>
        {summarizeWeek({ items, partnerName, sprintMode })}
      </Typography>
      {lastActivityLine ? (
        <Typography variant="body2" sx={{ mt: 0.4, color: "#70839a", fontWeight: 700 }}>
          {lastActivityLine}
        </Typography>
      ) : null}

      {!hasAnyPattern ? (
        <Typography variant="body2" sx={{ mt: 1.35, color: "#8a9cb1" }}>
          No activity yet this week.
        </Typography>
      ) : null}
    </Card>
  );
}
